/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Vect = require('../types/Vect')
const kernel = require('../kernel/kernel')
const CDroite = require('./CDroite')

module.exports = CBissectrice

/**
 * Classe bissectrice donnée par les trois sommes de l'angle.
 * @constructor
 * @extends CDroite
 * @param {CListeObjets} listeProprietaire : La liste propriétaire de l'objet.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire de l'objet.
 * @param {Boolean} estElementFinal : true si l'objet est un objet final de construction
 * @param {mtg32.Color} couleur : La couleur de l'objet.
 * @param {Boolean} nomMasque : true si le nom de l'objet est masqué
 * @param {Integer} dec_x : Décalage en abscisses du nom.
 * @param {Integer} dec_y : Décalage en ordonnées du nom.
 * @param {Boolean} masque : true si l'objet est masqué.
 * @param {String} nom : Le nom de l'objet. Seules les droites peuvent être nommées,
 * pas les segments ni demi-droites.
 * @param {Integer} tailleNom : Indice donnant la taille du nom.
 * @param {mtg32.StyleTrait} style : Le style de trait utilisé
 * @param {Number} abscisseNom : Abscisse du nom par rapport à la droite.
 * @param {CPt} b : Le premier point de l'angle.
 * @param {CPt} a : Le sommet de l'angle.
 * @param {CPt} c : Le troisième point de l'angle.
 * @returns {CBissectrice}
 */
function CBissectrice (listeProprietaire, impProto, estElementFinal, couleur, nomMasque,
  dec_x, dec_y, masque, nom, tailleNom, style, abscisseNom, b, a, c) {
  if (arguments.length === 1) CDroite.call(this, listeProprietaire);
  else {
    CDroite.call(this, listeProprietaire, impProto, estElementFinal, couleur,
      nomMasque, dec_x, dec_y, masque, nom, tailleNom, style, abscisseNom);
    this.b = b;
    this.a = a;
    this.c = c;
  }
  this.longueurVecteurDirecteur = 0.1;
}
CBissectrice.prototype = new CDroite();
CBissectrice.prototype.constructor = CBissectrice;
CBissectrice.prototype.superClass = "CDroite";
CBissectrice.prototype.className = "CBissectrice";

CBissectrice.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.b);
  var ind2 = listeSource.indexOf(this.a);
  var ind3 = listeSource.indexOf(this.c);
  var ind4 = listeSource.indexOf(this.impProto);
  return new CBissectrice(listeCible, listeCible.get(ind4, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.nomMasque, this.dec_x, this.dec_y, this.masque, this.nom, this.tailleNom,
    this.style, this.abscisseNom, listeCible.get(ind1, "CPt.class"),
    listeCible.get(ind2, "CPt"), listeCible.get(ind3, "CPt"));
};
CBissectrice.prototype.ajouteAntecedents= function(liste) {
  liste.add(this.b);
  liste.add(this.a);
  liste.add(this.c);
};
CBissectrice.prototype.positionne = function(infoRandom, dimfen) {
  var u = new Vect();
  var v = new Vect();
  var u1 = new Vect();
  var v1 = new Vect();

  this.existe = (this.a.existe) && (this.b.existe) && (this.c.existe)	&& (!this.a.egal(this.b)) && (!this.a.egal(this.c));
  if (!this.existe) return;
  this.point_x = this.a.x;
  this.point_y = this.a.y;
  u.setVecteur(this.a,this.b);
  v.setVecteur(this.a,this.c);
  // Correction d'un bug figurant jusque dans la version 2.5 du C++
  if (kernel.zeroAngle(Math.abs(u.mesureAngleVecteurs(v)) - Math.PI))
  {
    u.tourne(Math.PI/2, v);
    v.vecteurColineaire(this.longueurVecteurDirecteur, this.vect);
  }
  else {
    u.vecteurColineaire(1,u1);
    v.vecteurColineaire(1,v1);
    var w = new Vect();
    w.x = u1.x + v1.x;
    w.y = u1.y + v1.y;
    w.vecteurColineaire(this.longueurVecteurDirecteur, this.vect);
  }
  CDroite.prototype.positionne.call(this, infoRandom, dimfen);
};
CBissectrice.prototype.confonduAvec = function(p) {
  if (p.className === this.className) {
    return ((this.b===p.b) && (this.a===p.a) && (this.c===p.c))
            || ((this.b===p.c) && (this.a===p.a) && (this.c===p.b));
  } else return false;
};
CBissectrice.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CDroite.prototype.dependDe.call(this, p) ||
    this.b.dependDe(p) || this.a.dependDe(p) || this.c.dependDe(p));
};
CBissectrice.prototype.dependDePourBoucle = function(p) {
  return ((p === this) || this.b.dependDePourBoucle(p) || this.a.dependDePourBoucle(p) || this.c.dependDePourBoucle(p));
};
CBissectrice.prototype.remplacePoint = function(ancienPoint, nouveauPoint) {
  if (this.a === ancienPoint) this.a = nouveauPoint;
  if (this.b === ancienPoint) this.b = nouveauPoint;
  if (this.c === ancienPoint) this.c = nouveauPoint;
};
CBissectrice.prototype.contientParDefinition = function(po){
  return (this.a === po);
};
CBissectrice.prototype.read = function(inps, list){
  CDroite.prototype.read.call(this, inps, list);
   var ind1 = inps.readInt();
   var ind2 = inps.readInt();
   var ind3 = inps.readInt();
   this.b = list.get(ind1,"CPt");
   this.a = list.get(ind2,"CPt");
   this.c = list.get(ind3,"CPt");
};
CBissectrice.prototype.write = function(oups, list){
  CDroite.prototype.write.call(this, oups, list);
  var ind1 = list.indexOf(this.b);
  oups.writeInt(ind1);
  var ind2 = list.indexOf(this.a);
  oups.writeInt(ind2);
  var ind3 = list.indexOf(this.c);
  oups.writeInt(ind3);
};



// WEBPACK FOOTER //
// ./src/objets/CBissectrice.js